import { useState } from "react";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import Delete from "@mui/icons-material/Delete";
import Edit from "@mui/icons-material/Edit";
import Visibility from "@mui/icons-material/Visibility";
import {
  Autocomplete,
  Box,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  TextField as MUITextField,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { Formik } from "formik";
import { array, boolean, number, object, string } from "yup";

import { dayjs } from "@thalamos/common";

import { api, apiHooks, type FhirApiClientResponse } from "~/api.js";
import {
  Button,
  ButtonIcon,
  FormLabel,
  HelperText,
  LoadingSpinner,
  PopupDialog,
  PopupDialogTitle,
  renderErrorToast,
  renderSuccessToast,
  TextField,
} from "~/components/design-system/index.js";

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * The scopes this page offers, which must stay aligned with those the FHIR API
 * recognises: a scope missing here cannot be granted through the UI.
 *
 * Reading the MHA Episode and Forms API needs three of them together:
 * EpisodeOfCare for the timeline, DocumentReference for the form metadata, and
 * Binary for the documents themselves. They are separate so a consumer can be
 * given the timeline without the signed forms.
 */
const AVAILABLE_SCOPES = [
  "fhir:Flag.read",
  "fhir:Consent.read",
  "fhir:EpisodeOfCare.read",
  "fhir:DocumentReference.read",
  "fhir:Binary.read",
] as const;

// ─── Validation Schema ──────────────────────────────────────────────────────

const clientSchema = object({
  name: string().min(1, "Name is required").required("Name is required"),
  description: string().nullable(),
  publicKeyJwks: string()
    .required("Public key JWKS is required")
    .test("valid-json", "Must be valid JSON", (value) => {
      if (!value) return false;
      try {
        JSON.parse(value);
        return true;
      } catch {
        return false;
      }
    })
    .test("has-keys", "JWKS must contain at least one key", (value) => {
      if (!value) return false;
      try {
        const parsed = JSON.parse(value);
        // Accept either { keys: [...] } or a single key object
        if (parsed.keys && Array.isArray(parsed.keys))
          return parsed.keys.length > 0;
        return !!parsed.kty;
      } catch {
        return false;
      }
    }),
  allowedScopes: array()
    .of(string().required())
    .min(1, "At least one scope is required")
    .required(),
  retentionMonths: number()
    .min(1, "Must be at least 1 month")
    .max(120, "Must be at most 120 months")
    .required("Retention period is required"),
  isActive: boolean().required(),
});

type ClientFormValues = {
  name: string;
  description: string;
  publicKeyJwks: string;
  allowedOrganisationIds: string[];
  allowedScopes: string[];
  retentionMonths: number;
  isActive: boolean;
};

// ─── Delete confirmation modal ───────────────────────────────────────────────

function DeleteClientModal({
  open,
  onClose,
  client,
  onConfirm,
}: {
  open: boolean;
  onClose: () => void;
  client: FhirApiClientResponse;
  onConfirm: () => Promise<void>;
}) {
  return (
    <PopupDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete FHIR API Client</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to delete <strong>{client.name}</strong>? This
          action cannot be undone. Any systems using this client will lose
          access.
        </Typography>
      </DialogContent>
      <DialogActions
        sx={{ display: "flex", justifyContent: "space-between", px: 3 }}
      >
        <Button label="Cancel" onClick={onClose} />
        <Button
          label="Delete"
          onClick={async () => {
            await onConfirm();
            onClose();
          }}
        />
      </DialogActions>
    </PopupDialog>
  );
}

// ─── View JWKS modal ─────────────────────────────────────────────────────────

function ViewJwksModal({
  open,
  onClose,
  client,
}: {
  open: boolean;
  onClose: () => void;
  client: FhirApiClientResponse;
}) {
  return (
    <PopupDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Public Key JWKS — {client.name}</DialogTitle>
      <DialogContent>
        <Box
          component="pre"
          sx={{
            bgcolor: "grey.100",
            p: 2,
            borderRadius: 1,
            overflow: "auto",
            fontSize: "0.85rem",
            maxHeight: "60vh",
          }}
        >
          {JSON.stringify(client.publicKeyJwks, null, 2)}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        <Button
          label="Copy to Clipboard"
          onClick={() => {
            void navigator.clipboard.writeText(
              JSON.stringify(client.publicKeyJwks, null, 2),
            );
            renderSuccessToast({ message: "Copied to clipboard" });
          }}
        />
        <Button label="Close" onClick={onClose} />
      </DialogActions>
    </PopupDialog>
  );
}

// ─── Create / Edit modal ─────────────────────────────────────────────────────

type DataController = {
  id: string;
  name: string;
  odsCode: string | null;
};

function CreateEditClientModal({
  open,
  onClose,
  refetch,
  client,
  dataControllers,
}: {
  open: boolean;
  onClose: () => void;
  refetch: () => void;
  client?: FhirApiClientResponse;
  dataControllers: DataController[];
}) {
  const isEdit = !!client;

  const initialValues: ClientFormValues = {
    name: client?.name ?? "",
    description: client?.description ?? "",
    publicKeyJwks: client ? JSON.stringify(client.publicKeyJwks, null, 2) : "",
    allowedOrganisationIds: client?.allowedOrganisationIds ?? [],
    allowedScopes: client?.allowedScopes ?? [],
    retentionMonths: client?.retentionMonths ?? 6,
    isActive: client?.isActive ?? true,
  };

  return (
    <PopupDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <PopupDialogTitle
        titleText={
          isEdit ? `Edit Client — ${client.name}` : "Register New Client"
        }
        closeDialog={onClose}
      />
      <Formik<ClientFormValues>
        initialValues={initialValues}
        validationSchema={clientSchema}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            const parsedJwks = JSON.parse(values.publicKeyJwks);

            if (isEdit) {
              const result = await api.fhirApiClients.update(client.id, {
                name: values.name,
                description: values.description || null,
                publicKeyJwks: parsedJwks,
                allowedOrganisationIds: values.allowedOrganisationIds,
                allowedScopes: values.allowedScopes,
                retentionMonths: values.retentionMonths,
                isActive: values.isActive,
              });
              if (result.status >= 200 && result.status < 300) {
                renderSuccessToast({
                  message: `Client "${values.name}" updated successfully.`,
                });
              } else {
                renderErrorToast({ message: "Failed to update client." });
              }
            } else {
              const result = await api.fhirApiClients.create({
                name: values.name,
                description: values.description || null,
                publicKeyJwks: parsedJwks,
                allowedOrganisationIds: values.allowedOrganisationIds,
                allowedScopes: values.allowedScopes,
                retentionMonths: values.retentionMonths,
              });
              if (result.status >= 200 && result.status < 300) {
                renderSuccessToast({
                  message: `Client "${values.name}" created successfully.`,
                });
              } else {
                renderErrorToast({ message: "Failed to create client." });
              }
            }
            refetch();
            onClose();
          } catch {
            renderErrorToast({
              message: isEdit
                ? "Failed to update client."
                : "Failed to create client.",
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({ values, setValues, errors, touched, submitForm, isSubmitting }) => (
          <DialogContent>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Name */}
              <Box>
                <FormLabel
                  label="Name *"
                  error={!!touched.name && !!errors.name}
                />
                <TextField
                  name="name"
                  useFullWidth
                  value={values.name}
                  placeholder="e.g. London Care Record Production"
                  onChange={(e) => setValues({ ...values, name: e })}
                />
                {touched.name && errors.name && (
                  <HelperText errorMessage={errors.name} />
                )}
              </Box>

              {/* Description */}
              <Box>
                <FormLabel label="Description" />
                <TextField
                  name="description"
                  useFullWidth
                  multiline
                  rows="3"
                  value={values.description}
                  placeholder="Optional description of this API client"
                  onChange={(e) => setValues({ ...values, description: e })}
                />
              </Box>

              {/* Public Key JWKS */}
              <Box>
                <FormLabel
                  label="Public Key JWKS *"
                  error={!!touched.publicKeyJwks && !!errors.publicKeyJwks}
                />
                <TextField
                  name="publicKeyJwks"
                  useFullWidth
                  multiline
                  rows="8"
                  value={values.publicKeyJwks}
                  placeholder='Paste the client&#39;s public JWKS document, e.g. { "keys": [{ "kty": "EC", ... }] }'
                  onChange={(e) => setValues({ ...values, publicKeyJwks: e })}
                />
                {touched.publicKeyJwks && errors.publicKeyJwks && (
                  <HelperText errorMessage={errors.publicKeyJwks} />
                )}
              </Box>

              {/* Allowed Data Controllers */}
              <Box>
                <FormLabel
                  label="Allowed Data Controllers *"
                  error={
                    !!touched.allowedOrganisationIds &&
                    !!errors.allowedOrganisationIds
                  }
                />
                <Autocomplete
                  multiple
                  options={dataControllers}
                  getOptionLabel={(option) =>
                    option.odsCode
                      ? `${option.name} (${option.odsCode})`
                      : option.name
                  }
                  value={dataControllers.filter((dc) =>
                    values.allowedOrganisationIds.includes(dc.id),
                  )}
                  onChange={(_event, newValue) => {
                    setValues({
                      ...values,
                      allowedOrganisationIds: newValue.map((dc) => dc.id),
                    });
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <MUITextField
                      {...params}
                      placeholder="Select data controllers this client may query"
                      size="small"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={option.id}
                        label={
                          option.odsCode
                            ? `${option.name} (${option.odsCode})`
                            : option.name
                        }
                        size="small"
                      />
                    ))
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  Only organisations that are their own data controllers are
                  shown. Clients with no allowed data controllers will always
                  receive empty results.
                </Typography>
                {touched.allowedOrganisationIds &&
                  errors.allowedOrganisationIds && (
                    <HelperText
                      errorMessage={errors.allowedOrganisationIds as string}
                    />
                  )}
              </Box>

              {/* Allowed Scopes */}
              <Box>
                <FormLabel
                  label="Allowed Scopes *"
                  error={!!touched.allowedScopes && !!errors.allowedScopes}
                />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  {AVAILABLE_SCOPES.map((scope) => (
                    <Chip
                      key={scope}
                      label={scope}
                      clickable
                      color={
                        values.allowedScopes.includes(scope)
                          ? "primary"
                          : "default"
                      }
                      variant={
                        values.allowedScopes.includes(scope)
                          ? "filled"
                          : "outlined"
                      }
                      onClick={() => {
                        const next = values.allowedScopes.includes(scope)
                          ? values.allowedScopes.filter((s) => s !== scope)
                          : [...values.allowedScopes, scope];
                        setValues({ ...values, allowedScopes: next });
                      }}
                    />
                  ))}
                </Box>
                {touched.allowedScopes && errors.allowedScopes && (
                  <HelperText errorMessage={errors.allowedScopes as string} />
                )}
              </Box>

              {/* Retention Months */}
              <Box>
                <FormLabel
                  label="Retention Window (months) *"
                  error={!!touched.retentionMonths && !!errors.retentionMonths}
                />
                <TextField
                  name="retentionMonths"
                  useFullWidth
                  value={String(values.retentionMonths)}
                  onChange={(e) =>
                    setValues({
                      ...values,
                      retentionMonths: e === "" ? 6 : Number(e),
                    })
                  }
                />
                {touched.retentionMonths && errors.retentionMonths && (
                  <HelperText errorMessage={errors.retentionMonths as string} />
                )}
              </Box>

              {/* Active toggle (edit only) */}
              {isEdit && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>Active</Typography>
                  <Switch
                    checked={values.isActive}
                    onChange={(e) =>
                      setValues({
                        ...values,
                        isActive: e.target.checked,
                      })
                    }
                  />
                </Box>
              )}
            </Box>

            <DialogActions
              sx={{
                display: "flex",
                justifyContent: "space-between",
                px: 0,
                mt: 3,
              }}
            >
              <Button label="Cancel" onClick={onClose} />
              <Button
                label={isEdit ? "Save Changes" : "Create Client"}
                onClick={submitForm}
                disabled={isSubmitting}
              />
            </DialogActions>
          </DialogContent>
        )}
      </Formik>
    </PopupDialog>
  );
}

// ─── Client row ──────────────────────────────────────────────────────────────

function FhirApiClientRow({
  client,
  onEdit,
  onDelete,
  onViewJwks,
  onToggleActive,
}: {
  client: FhirApiClientResponse;
  onEdit: () => void;
  onDelete: () => void;
  onViewJwks: () => void;
  onToggleActive: () => void;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  return (
    <TableRow>
      <TableCell>
        <Typography fontWeight={600}>{client.name}</Typography>
        {client.description && (
          <Typography variant="body2" color="text.secondary">
            {client.description}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <Tooltip title="Click to copy">
          <Box
            component="code"
            onClick={() => {
              void navigator.clipboard.writeText(client.id);
              renderSuccessToast({ message: "Client ID copied" });
            }}
            sx={{
              cursor: "pointer",
              fontSize: "0.8rem",
              bgcolor: "grey.100",
              px: 0.5,
              borderRadius: 0.5,
            }}
          >
            {client.id}
          </Box>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
          {client.allowedScopes.map((scope) => (
            <Chip key={scope} label={scope} size="small" variant="outlined" />
          ))}
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label={client.isActive ? "Active" : "Inactive"}
          size="small"
          color={client.isActive ? "success" : "default"}
          variant="filled"
        />
      </TableCell>
      <TableCell>{client.retentionMonths} months</TableCell>
      <TableCell>{dayjs(client.createdAt).format("D MMM YYYY")}</TableCell>
      <TableCell>
        <IconButton
          size="small"
          aria-label="Client actions"
          onClick={(e) => setAnchorEl(e.currentTarget)}
        >
          •••
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onEdit();
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onViewJwks();
            }}
          >
            <ListItemIcon>
              <Visibility fontSize="small" />
            </ListItemIcon>
            View JWKS
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              void navigator.clipboard.writeText(client.id);
              renderSuccessToast({ message: "Client ID copied" });
            }}
          >
            <ListItemIcon>
              <ContentCopyIcon fontSize="small" />
            </ListItemIcon>
            Copy Client ID
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onToggleActive();
            }}
          >
            <ListItemIcon>
              <Switch checked={client.isActive} size="small" readOnly />
            </ListItemIcon>
            {client.isActive ? "Deactivate" : "Activate"}
          </MenuItem>
          <MenuItem
            onClick={() => {
              setAnchorEl(null);
              onDelete();
            }}
          >
            <ListItemIcon>
              <Delete fontSize="small" />
            </ListItemIcon>
            Delete
          </MenuItem>
        </Menu>
      </TableCell>
    </TableRow>
  );
}

// ─── Main page ───────────────────────────────────────────────────────────────

export function FhirApiClientManagementPage() {
  const [{ data: clients, loading }, refetch] =
    apiHooks.fhirApiClients.getAll();
  const [{ data: dataControllers }] =
    apiHooks.fhirApiClients.getAvailableDataControllers();
  const [editClient, setEditClient] = useState<
    FhirApiClientResponse | undefined
  >();
  const [deleteClient, setDeleteClient] = useState<
    FhirApiClientResponse | undefined
  >();
  const [viewJwksClient, setViewJwksClient] = useState<
    FhirApiClientResponse | undefined
  >();
  const [showCreate, setShowCreate] = useState(false);

  const handleDelete = async (client: FhirApiClientResponse) => {
    try {
      const result = await api.fhirApiClients.delete(client.id);
      if (result.status >= 200 && result.status < 300) {
        renderSuccessToast({
          message: `Client "${client.name}" deleted.`,
        });
      } else {
        renderErrorToast({ message: "Failed to delete client." });
      }
      void refetch();
    } catch {
      renderErrorToast({ message: "Failed to delete client." });
    }
  };

  const handleToggleActive = async (client: FhirApiClientResponse) => {
    try {
      const result = await api.fhirApiClients.update(client.id, {
        isActive: !client.isActive,
      });
      if (result.status >= 200 && result.status < 300) {
        renderSuccessToast({
          message: client.isActive
            ? `Client "${client.name}" deactivated.`
            : `Client "${client.name}" activated.`,
        });
      } else {
        renderErrorToast({ message: "Failed to update client status." });
      }
      void refetch();
    } catch {
      renderErrorToast({ message: "Failed to update client status." });
    }
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h3">FHIR API Clients</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Manage OAuth2 client registrations for external FHIR API consumers.
            Each client uses a public/private keypair for authentication.
          </Typography>
        </Box>
        <Button
          endIcon={ButtonIcon.add}
          label="Register Client"
          onClick={() => setShowCreate(true)}
        />
      </Box>

      {loading || !clients ? (
        <LoadingSpinner />
      ) : clients.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            bgcolor: "grey.50",
            borderRadius: 1,
          }}
        >
          <Typography variant="h6" color="text.secondary">
            No FHIR API clients registered
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Click &ldquo;Register Client&rdquo; to add a new external API
            consumer.
          </Typography>
        </Box>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Client ID</TableCell>
                <TableCell>Scopes</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Retention</TableCell>
                <TableCell>Created</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {clients.map((client) => (
                <FhirApiClientRow
                  key={client.id}
                  client={client}
                  onEdit={() => setEditClient(client)}
                  onDelete={() => setDeleteClient(client)}
                  onViewJwks={() => setViewJwksClient(client)}
                  onToggleActive={() => void handleToggleActive(client)}
                />
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create modal */}
      {showCreate && (
        <CreateEditClientModal
          open
          onClose={() => setShowCreate(false)}
          refetch={() => void refetch()}
          dataControllers={dataControllers ?? []}
        />
      )}

      {/* Edit modal */}
      {editClient && (
        <CreateEditClientModal
          open
          onClose={() => setEditClient(undefined)}
          refetch={() => void refetch()}
          client={editClient}
          dataControllers={dataControllers ?? []}
        />
      )}

      {/* Delete modal */}
      {deleteClient && (
        <DeleteClientModal
          open
          onClose={() => setDeleteClient(undefined)}
          client={deleteClient}
          onConfirm={() => handleDelete(deleteClient)}
        />
      )}

      {/* View JWKS modal */}
      {viewJwksClient && (
        <ViewJwksModal
          open
          onClose={() => setViewJwksClient(undefined)}
          client={viewJwksClient}
        />
      )}
    </Box>
  );
}
